/**
 * Add Photo Modal Component
 * For adding geolocated photos to the map
 */

import { useState, useEffect, useRef } from 'react';
import type { PhotoCategory, PhotoData, PhotoType, PhotoArea } from '../../../../shared/types/project';
import { DEFAULT_PHOTO_CATEGORIES, PHOTO_TYPES, PHOTO_AREAS, DEFAULT_LEVELS } from '../../../../shared/types/project';
import { useFilePicker, FILE_ACCEPTS } from '../../../hooks/useFilePicker';
import { api } from '../../../services/api';
import { LocalImage } from '../../../components/common/LocalImage';
import '../../../components/modals/ProjectModal.css';

interface AddPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (photo: Omit<PhotoData, 'id' | 'addedAt'>) => void;
  onPlaceOnMap?: (photoData: Omit<PhotoData, 'id' | 'addedAt' | 'coordinates'>) => void;  // Callback to enter "place on map" mode
  customCategories?: PhotoCategory[];
  defaultCoordinates?: { longitude: number; latitude: number };
  editingPhoto?: PhotoData;  // If provided, we're editing instead of adding
}

export function AddPhotoModal({
  isOpen,
  onClose,
  onAdd,
  onPlaceOnMap,
  customCategories = [],
  defaultCoordinates,
  editingPhoto,
}: AddPhotoModalProps) {
  const { pickFiles } = useFilePicker();
  const [filePath, setFilePath] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [categoryId, setCategoryId] = useState('general');
  const [photoType, setPhotoType] = useState<PhotoType | string>('');
  const [photoArea, setPhotoArea] = useState<PhotoArea | string>('');
  const [level, setLevel] = useState('');
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [timestamp, setTimestamp] = useState<string | undefined>();
  const [thumbnailPath, setThumbnailPath] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [exifError, setExifError] = useState<string | null>(null);
  const [hasGps, setHasGps] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const allCategories = [...DEFAULT_PHOTO_CATEGORIES, ...customCategories];
  const isEditing = !!editingPhoto;

  // Reset or populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editingPhoto) {
        // Populate form for editing
        setFilePath(editingPhoto.path);
        setName(editingPhoto.name);
        setDescription(editingPhoto.description || '');
        setNotes(editingPhoto.notes || '');
        setCategoryId(editingPhoto.categoryId);
        setPhotoType(editingPhoto.photoType || '');
        setPhotoArea(editingPhoto.photoArea || '');
        setLevel(editingPhoto.level || '');
        setLongitude(editingPhoto.coordinates.longitude.toString());
        setLatitude(editingPhoto.coordinates.latitude.toString());
        setTimestamp(editingPhoto.timestamp);
        setThumbnailPath(editingPhoto.thumbnailPath);
        setHasGps(!editingPhoto.isManuallyPlaced);
        setExifError(null);
      } else {
        // Reset form for new photo
        setFilePath('');
        setName('');
        setDescription('');
        setNotes('');
        setCategoryId('general');
        setPhotoType('');
        setPhotoArea('');
        setLevel('');
        setLongitude(defaultCoordinates?.longitude?.toString() || '');
        setLatitude(defaultCoordinates?.latitude?.toString() || '');
        setTimestamp(undefined);
        setThumbnailPath(undefined);
        setExifError(null);
        setHasGps(false);
        setShowAdvanced(false);
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, defaultCoordinates, editingPhoto]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSelectFile = async () => {
    const result = await pickFiles({
      accept: FILE_ACCEPTS.PHOTO,
    });

    if (result.canceled || !result.files[0]) return;

    const file = result.files[0];

    // Upload the file to the server
    setIsLoading(true);
    try {
      const uploaded = await api.file.upload(file);
      const path = uploaded.storagePath;
      setFilePath(path);
      setExifError(null);

      // Extract filename for name
      const baseName = file.name.replace(/\.[^.]+$/, '');
      if (!name) setName(baseName);

      // Read EXIF data
      const exifResult: any = await api.photo.readExif(path);
      if (exifResult.success && exifResult.data) {
        const { latitude: lat, longitude: lng, timestamp: ts } = exifResult.data;

        if (lat !== undefined && lng !== undefined) {
          setLatitude(lat.toString());
          setLongitude(lng.toString());
          setHasGps(true);
          setExifError(null);
        } else {
          setHasGps(false);
          if (!defaultCoordinates && !editingPhoto) {
            setExifError('No GPS data found. You can enter coordinates manually or place the photo on the map.');
          }
        }

        if (ts) {
          setTimestamp(ts);
        }
      }

      // Generate thumbnail
      const thumbResult: any = await api.photo.generateThumbnail(path, 200);
      if (thumbResult.success && thumbResult.thumbnailPath) {
        setThumbnailPath(thumbResult.thumbnailPath);
      }
    } catch (error) {
      console.error('Failed to process photo:', error);
      setExifError('Failed to read image metadata.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlaceOnMap = () => {
    console.log('[AddPhotoModal] handlePlaceOnMap called', { onPlaceOnMap: !!onPlaceOnMap, filePath, name: name.trim() });
    if (onPlaceOnMap && filePath && name.trim()) {
      // Pass the partial photo data (without coordinates)
      const photoData = {
        name: name.trim(),
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        path: filePath,
        thumbnailPath,
        categoryId,
        photoType: photoType || undefined,
        photoArea: photoArea || undefined,
        level: level || undefined,
        isManuallyPlaced: true,
        timestamp,
      };
      console.log('[AddPhotoModal] Calling onPlaceOnMap with:', photoData);
      onPlaceOnMap(photoData);
      onClose();
    } else {
      console.log('[AddPhotoModal] Cannot place on map - missing data');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const lng = parseFloat(longitude);
    const lat = parseFloat(latitude);

    if (!filePath || !name.trim() || isNaN(lng) || isNaN(lat)) {
      return;
    }

    onAdd({
      name: name.trim(),
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      path: filePath,
      thumbnailPath,
      categoryId,
      photoType: photoType || undefined,
      photoArea: photoArea || undefined,
      level: level || undefined,
      coordinates: { longitude: lng, latitude: lat },
      isManuallyPlaced: !hasGps,
      timestamp,
    });

    onClose();
  };

  const isValid = filePath && name.trim() && !isNaN(parseFloat(longitude)) && !isNaN(parseFloat(latitude));

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '540px', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{isEditing ? 'Edit Photo' : 'Add Photo'}</h2>
          <button className="modal__close" onClick={onClose} title="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className="modal__body" onSubmit={handleSubmit}>
          {/* File Selection */}
          {!isEditing && (
            <div className="form-group">
              <label className="label">Photo File *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="button"
                  onClick={handleSelectFile}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Select Photo'}
                </button>
                {filePath && (
                  <span style={{
                    flex: 1,
                    color: 'var(--text-secondary)',
                    fontSize: 'var(--font-sm)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    alignSelf: 'center'
                  }}>
                    {filePath.split(/[\\/]/).pop()}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Thumbnail Preview */}
          {thumbnailPath && (
            <div className="form-group" style={{ display: 'flex', justifyContent: 'center' }}>
              <LocalImage
                src={thumbnailPath}
                alt="Preview"
                style={{
                  maxWidth: '200px',
                  maxHeight: '150px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-default)',
                }}
              />
            </div>
          )}

          {/* Name */}
          <div className="form-group">
            <label className="label" htmlFor="photo-name">Name *</label>
            <input
              ref={inputRef}
              id="photo-name"
              type="text"
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter photo name..."
              disabled={isLoading}
            />
          </div>

          {/* Category & Type Row */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label" htmlFor="photo-category">Category *</label>
              <select
                id="photo-category"
                className="input"
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
                disabled={isLoading}
              >
                {allCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label className="label" htmlFor="photo-type">Photo Type</label>
              <select
                id="photo-type"
                className="input"
                value={photoType}
                onChange={e => setPhotoType(e.target.value)}
                disabled={isLoading}
              >
                <option value="">Select type...</option>
                {PHOTO_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Area & Level Row */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label" htmlFor="photo-area">Photo Area</label>
              <select
                id="photo-area"
                className="input"
                value={photoArea}
                onChange={e => setPhotoArea(e.target.value)}
                disabled={isLoading}
              >
                <option value="">Select area...</option>
                {PHOTO_AREAS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label className="label" htmlFor="photo-level">Level/Floor</label>
              <select
                id="photo-level"
                className="input"
                value={level}
                onChange={e => setLevel(e.target.value)}
                disabled={isLoading}
              >
                <option value="">Select level...</option>
                {DEFAULT_LEVELS.map(lvl => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="label" htmlFor="photo-notes">Notes</label>
            <textarea
              id="photo-notes"
              className="input"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add notes..."
              rows={2}
              style={{ resize: 'vertical' }}
              disabled={isLoading}
            />
          </div>

          {/* Coordinates */}
          <div className="form-group">
            <label className="label">
              Location *
              {hasGps && (
                <span style={{
                  marginLeft: '8px',
                  color: 'var(--success)',
                  fontSize: 'var(--font-xs)',
                  fontWeight: 'normal'
                }}>
                  (from EXIF)
                </span>
              )}
            </label>
            {exifError && (
              <p style={{
                color: 'var(--warning)',
                fontSize: 'var(--font-xs)',
                margin: '0 0 8px 0',
              }}>
                {exifError}
              </p>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  className="input"
                  value={longitude}
                  onChange={e => setLongitude(e.target.value)}
                  placeholder="Longitude"
                  disabled={isLoading}
                />
                <span style={{
                  fontSize: 'var(--font-xs)',
                  color: 'var(--text-tertiary)',
                  marginTop: '4px',
                  display: 'block'
                }}>
                  Longitude (e.g., -122.4194)
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  className="input"
                  value={latitude}
                  onChange={e => setLatitude(e.target.value)}
                  placeholder="Latitude"
                  disabled={isLoading}
                />
                <span style={{
                  fontSize: 'var(--font-xs)',
                  color: 'var(--text-tertiary)',
                  marginTop: '4px',
                  display: 'block'
                }}>
                  Latitude (e.g., 37.7749)
                </span>
              </div>
            </div>
            {/* Always show Place on Map button when available */}
            {onPlaceOnMap && filePath && name.trim() && (
              <button
                type="button"
                className="button button--primary"
                style={{ marginTop: '12px', width: '100%' }}
                onClick={handlePlaceOnMap}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Place on Map
              </button>
            )}
          </div>

          {/* Advanced Section */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--accent-primary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: 'var(--font-sm)',
              padding: '8px 0',
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transform: showAdvanced ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
            {showAdvanced ? 'Less' : 'More...'}
          </button>

          {showAdvanced && (
            <div className="form-group">
              <label className="label" htmlFor="photo-description">Description</label>
              <textarea
                id="photo-description"
                className="input"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Detailed description..."
                rows={3}
                style={{ resize: 'vertical' }}
                disabled={isLoading}
              />
            </div>
          )}

          <div className="modal__actions">
            <button
              type="button"
              className="button"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={!isValid || isLoading}
            >
              {isEditing ? 'Save Changes' : 'Add Photo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
